import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeService } from '@/services/knowledge.service';
import { UploadCloud, File, Trash2, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ManageDocuments() {
  const { id: storeId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const { data: documentStore, isLoading, error } = useQuery({
    queryKey: ['documentStore', storeId],
    queryFn: () => knowledgeService.getDocumentById(storeId!),
    enabled: !!storeId,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => knowledgeService.uploadFile(file),
    onSuccess: (uploadData, file) => {
      const currentDocuments = documentStore?.documents || [];
      const newDocument = {
        name: file.name,
        key: uploadData.key,
        url: uploadData.url,
        fileType: file.type,
        use_as_example: false,
      };
      const updatedDocuments = [...currentDocuments, newDocument];
      updateStoreMutation.mutate({ document_urls: updatedDocuments });
    },
    onError: () => toast.error('File upload failed.'),
  });

  const updateStoreMutation = useMutation({
    mutationFn: (updatedData) => knowledgeService.updateDocument(storeId!, updatedData),
    onSuccess: () => {
      toast.success('Document list updated!');
      queryClient.invalidateQueries({ queryKey: ['documentStore', storeId] });
      setFile(null);
    },
    onError: () => toast.error('Failed to update document store.'),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (file) {
      uploadMutation.mutate(file);
    }
  };

  const handleDelete = (docKey: string) => {
    const updatedDocuments = documentStore.documents.filter(d => d.key !== docKey);
    updateStoreMutation.mutate({ document_urls: updatedDocuments });
  };

  const handleToggleExample = (docKey: string) => {
    const updatedDocuments = documentStore.documents.map(doc => {
      if (doc.key === docKey) {
        return { ...doc, use_as_example: !doc.use_as_example };
      }
      return doc;
    });
    updateStoreMutation.mutate({ document_urls: updatedDocuments });
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading document store.</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/knowledge/documents" className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800">
          <ArrowLeft size={16} />
          Back to Knowledge Stores
        </Link>
        <h1 className="text-3xl font-bold mt-2">Manage Documents: {documentStore?.name}</h1>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Upload New Document</h2>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <UploadCloud size={48} className="mx-auto text-gray-400" />
          <p className="mt-4 text-lg font-semibold">Drag & drop files here or</p>
          <input type="file" onChange={handleFileChange} className="hidden" id="file-upload" />
          <label htmlFor="file-upload" className="btn btn-secondary mt-4 cursor-pointer">
            Browse Files
          </label>
          {file && <p className="mt-2 text-sm text-gray-600">Selected: {file.name}</p>}
          <button onClick={handleUpload} className="btn btn-primary mt-4 ml-4" disabled={!file || uploadMutation.isLoading}>
            {uploadMutation.isLoading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <h2 className="text-lg font-semibold mb-4 p-6">Uploaded Documents</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Uploaded</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Use as Example</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documentStore?.documents.map((doc) => (
                <tr key={doc._id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 font-medium text-gray-900">{doc.name}</td>
                  <td className="px-4 py-4"><span className="badge badge-success capitalize">{doc.status}</span></td>
                  <td className="px-4 py-4 text-sm text-gray-600">{new Date(doc.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-4 text-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={doc.use_as_example || false}
                      onChange={() => handleToggleExample(doc.key)}
                    />
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button onClick={() => handleDelete(doc.key)} className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
